import { useState } from 'react';
import { SavedWorkOrder, AppSettings } from '../types';
import { formatCurrency, formatDate, exportSingleWorkOrder } from '../utils';
import { useTranslation } from 'react-i18next';
import { companyPresets, printLanguages } from '../config';

interface WorkOrderPrintViewProps {
  workOrder: SavedWorkOrder;
  settings: AppSettings;
  onClose: () => void;
  onSave?: () => SavedWorkOrder | null; // Returns updated work order if saved, null if no changes
  hasUnsavedChanges?: boolean;
}

const WorkOrderPrintView = ({ workOrder, settings, onClose, onSave, hasUnsavedChanges = false }: WorkOrderPrintViewProps) => {
  const { t: tUI, i18n } = useTranslation();
  
  // State for print language
  const [printLanguage, setPrintLanguage] = useState(
    workOrder.settings.printLanguage || settings.printLanguage || 'en'
  );
  // State for optional project ID
  const [projectID, setProjectID] = useState('');
  // State for custom document number override
  const [customDocumentNumber, setCustomDocumentNumber] = useState('');
  // State for payment terms
  const [paymentTermsDays, setPaymentTermsDays] = useState(settings.paymentTermsDays || 14);
  
  const t = i18n.getFixedT(printLanguage);

  // Map print language to country code for company details
  const langToCountry: Record<string, keyof typeof companyPresets> = {
    fi: 'fi',
    sv: 'se',
    no: 'no',
    da: 'dk', // Danish uses Danish company (Halton A/S)
    pl: 'pl',
    nl: 'nl',
    de: 'de',
    fr: 'fr',
    en: 'gb',
  };

  // Get currency country from selected print language
  const currencyCountry = langToCountry[printLanguage] || settings.companyCountry;

  const company = companyPresets[currencyCountry] || companyPresets[settings.companyCountry];

  // Use custom document number if provided, otherwise use the original
  const displayDocumentNumber = customDocumentNumber || workOrder.documentNumber;

  // Save first if there are unsaved changes, then return the latest work order
  const saveIfNeeded = (): SavedWorkOrder => {
    if (hasUnsavedChanges && onSave) {
      const savedWorkOrder = onSave();
      if (savedWorkOrder) {
        return savedWorkOrder;
      }
    }
    return workOrder;
  };

  const handlePrint = () => {
    saveIfNeeded();
    window.print();
  };

  const handleExport = () => {
    const workOrderToExport = saveIfNeeded();
    exportSingleWorkOrder(workOrderToExport);
  };

  return (
    <div className="print-view">
      {/* Print Controls - Hidden when printing */}
      <div className="print-controls no-print">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            ← {tUI('common.back')}
          </button>

          {/* Language Selector */}
          <select
            value={printLanguage}
            onChange={(e) => setPrintLanguage(e.target.value as any)}
            className="form-select"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            title={t('settings.print.language')}
          >
            {printLanguages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName}
              </option>
            ))}
          </select>

          {/* Payment Terms Dropdown */}
          <select
            value={paymentTermsDays}
            onChange={(e) => setPaymentTermsDays(Number(e.target.value))}
            className="form-select"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            title={t('print.settings.paymentTerms')}
          >
            <option value={14}>14 {t('print.settings.days')} net</option>
            <option value={21}>21 {t('print.settings.days')} net</option>
            <option value={30}>30 {t('print.settings.days')} net</option>
          </select>

          {/* Project ID Input */}
          <input
            type="text"
            value={projectID}
            onChange={(e) => setProjectID(e.target.value)}
            placeholder={t('print.settings.projectID')}
            className="form-input"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '120px' }}
          />

          {/* Custom Document Number Input */}
          <input
            type="text"
            value={customDocumentNumber}
            onChange={(e) => setCustomDocumentNumber(e.target.value)}
            placeholder={t('print.header.documentNumber')}
            className="form-input"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '140px' }}
          />
        </div>

        <button className="btn btn-secondary" onClick={handleExport} title={t('archive.exportJson')}>
          📤 {t('archive.exportJson')}
        </button>
        <button className="btn btn-primary" onClick={handlePrint}>
          🖨️ {tUI('common.printPdf')}
        </button>
        {hasUnsavedChanges && (
          <span style={{ fontSize: '0.75rem', color: '#f59e0b', marginLeft: '8px' }}>
            ⚠️ {t('workOrder.unsavedChanges')}
          </span>
        )}
        <span className="print-tip" style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '12px' }}>
          💡 {t('print.tip.saveasPdf')}
        </span>
      </div>

      <div className="print-document work-order-document">
        {/* Header */}
        <header className="print-header">
          <div className="print-header-left">
            <img src="/halton-logo.png" alt="Halton" className="print-logo" style={{ height: '32px', marginBottom: '8px' }} />
            <div className="company-info">
              {company.name}<br />
              {company.address}<br />
              {company.postalCode} {company.city}<br />
              {t('print.company.vat')}: {company.vatId}
            </div>
          </div>

          <div className="print-header-right">
            <h1 className="document-title">{t('print.workOrder.title')}</h1>
            <div className="document-meta">
              <div className="meta-row">
                <span className="meta-label">{t('print.header.documentNumber')}:</span>
                <span className="meta-value">{displayDocumentNumber}</span>
              </div>
              {workOrder.formData.jobNumber && (
                <div className="meta-row">
                  <span className="meta-label">{t('print.workOrder.jobNumber')}:</span>
                  <span className="meta-value">{workOrder.formData.jobNumber}</span>
                </div>
              )}
              <div className="meta-row">
                <span className="meta-label">{t('print.header.date')}:</span>
                <span className="meta-value">{formatDate(workOrder.createdAt, printLanguage)}</span>
              </div>
              {projectID && (
                <div className="meta-row">
                  <span className="meta-label">{t('print.header.projectID')}:</span>
                  <span className="meta-value">{projectID}</span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Billing Information */}
        <div className="info-section">
          <h3 className="section-title">{t('print.workOrder.billingInformation').toUpperCase()}</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">{t('print.customer.name')}:</span>
              <span className="info-value">{workOrder.formData.yritys || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.billing.vatId')}:</span>
              <span className="info-value">{workOrder.formData.ytunnus || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('print.customer.address')}:</span>
              <span className="info-value">{workOrder.formData.osoite || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.billing.postalCode')}:</span>
              <span className="info-value">{workOrder.formData.postinumero || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.billing.city')}:</span>
              <span className="info-value">{workOrder.formData.paikkakunta || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.billing.customerNumber')}:</span>
              <span className="info-value">{workOrder.formData.asiakasnumero || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('print.workOrder.customerReference')}:</span>
              <span className="info-value">{workOrder.formData.viite || t('common.notAvailable')}</span>
            </div>
            {workOrder.formData.tilaaja && (
              <div className="info-item">
                <span className="info-label">{t('workOrder.billing.orderer')}:</span>
                <span className="info-value">{workOrder.formData.tilaaja}</span>
              </div>
            )}
            {workOrder.formData.laskutusosoite && (
              <div className="info-item">
                <span className="info-label">{t('workOrder.billing.billingAddress')}:</span>
                <span className="info-value">{workOrder.formData.laskutusosoite}</span>
              </div>
            )}
            {(workOrder.formData.verkkolaskuosoite || workOrder.formData.ovt) && (
              <div className="info-item">
                <span className="info-label">{t('workOrder.billing.ovt')}:</span>
                <span className="info-value">{workOrder.formData.verkkolaskuosoite || workOrder.formData.ovt}</span>
              </div>
            )}
            {(workOrder.formData.verkkolaskuoperaattori || workOrder.formData.operaattori) && (
              <div className="info-item">
                <span className="info-label">{t('workOrder.billing.operator')}:</span>
                <span className="info-value">{workOrder.formData.verkkolaskuoperaattori || workOrder.formData.operaattori}</span>
              </div>
            )}
          </div>
        </div>

        {/* Site & Order Details */}
        <div className="info-section">
          <h3 className="section-title">{t('print.workOrder.siteOrderDetails').toUpperCase()}</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">{t('print.workOrder.orderNumber')}:</span>
              <span className="info-value">{workOrder.formData.tilausnumero || t('common.notAvailable')}</span>
            </div>
            {workOrder.formData.ourReference && (
              <div className="info-item">
                <span className="info-label">{t('print.workOrder.ourReference')}:</span>
                <span className="info-value">{workOrder.formData.ourReference}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">{t('print.workOrder.siteName')}:</span>
              <span className="info-value">{workOrder.formData.kohde || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('print.customer.address')}:</span>
              <span className="info-value">{workOrder.formData.kohdeOsoite || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.site.sitePostalCode')}:</span>
              <span className="info-value">{workOrder.formData.kohdePostinumero || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.site.siteCity')}:</span>
              <span className="info-value">{workOrder.formData.kohdePaikkakunta || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('print.customer.contact')}:</span>
              <span className="info-value">{workOrder.formData.yhteyshenkilo || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('print.customer.phone')}:</span>
              <span className="info-value">{workOrder.formData.puhelin || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('print.customer.email')}:</span>
              <span className="info-value">{workOrder.formData.sahkoposti || t('common.notAvailable')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('workOrder.site.date')}:</span>
              <span className="info-value">{workOrder.formData.ajankohta || t('common.notAvailable')}</span>
            </div>
            {workOrder.formData.lineOfBusiness && (
              <div className="info-item">
                <span className="info-label">{t('workOrder.order.lineOfBusiness')}:</span>
                <span className="info-value">{workOrder.formData.lineOfBusiness.toUpperCase()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Work Description - only show if description exists */}
        {workOrder.formData.kuvaus && (
          <div className="info-section">
            <h3 className="section-title">{t('print.workOrder.workDescription').toUpperCase()}</h3>
            <div className="description-box">
              {workOrder.formData.kuvaus}
            </div>
          </div>
        )}

        {/* Additional Information */}
        {workOrder.formData.lisatietoja && (
          <div className="info-section">
            <h3 className="section-title">{t('print.workOrder.additionalInfo').toUpperCase()}</h3>
            <div className="description-box">
              {workOrder.formData.lisatietoja}
            </div>
          </div>
        )}

        {/* Technician Information */}
        {workOrder.formData.teknikonNimi && (
          <div className="info-section">
            <h3 className="section-title">{t('print.workOrder.technicianName').toUpperCase()}</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">{t('workOrder.technician.name')}:</span>
                <span className="info-value">{workOrder.formData.teknikonNimi}</span>
              </div>
              {workOrder.formData.paivamaara && (
                <div className="info-item">
                  <span className="info-label">{t('print.workOrder.date')}:</span>
                  <span className="info-value">{workOrder.formData.paivamaara}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detailed Pricing Items */}
        {(workOrder.travelItems.length > 0 || workOrder.workItems.length > 0 ||
          workOrder.parts.length > 0 || workOrder.customLines.length > 0) && (
            <div className="pricing-section">
              <h3 className="section-title">{t('print.workOrder.billingItems').toUpperCase()}</h3>
              <table className="print-table">
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>{t('print.pricing.description')}</th>
                    <th style={{ width: '15%', textAlign: 'center' }}>{t('print.pricing.quantity')}</th>
                    <th style={{ width: '15%', textAlign: 'right' }}>{t('print.pricing.unitPrice')}</th>
                    <th style={{ width: '20%', textAlign: 'right' }}>{t('print.pricing.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Custom Lines */}
                  {workOrder.customLines.map((line, index) => {
                    const discount = line.discountPercent || 0;
                    const discountedPrice = line.price * (1 - discount / 100);
                    return (
                      <tr key={`custom-${index}`}>
                        <td>
                          {line.description}
                          {discount > 0 && (
                            <span className="item-desc"> (-{discount}% {t('print.travel.discount')})</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>1 {t('print.pricing.pcs')}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(line.price, currencyCountry)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(discountedPrice, currencyCountry)}</td>
                      </tr>
                    );
                  })}

                  {/* Travel Items - Split into km cost and time cost rows */}
                  {workOrder.travelItems.length > 0 && workOrder.travelItems.map((item, index) => {
                    if (item.mode === 'fixed') {
                      // Fixed price mode - single row
                      const fixedCost = item.fixedPrice * (1 - (item.discountPercent || 0) / 100);
                      return (
                        <tr key={`travel-${index}`}>
                          <td>
                            <strong>{t('print.travel.title')} {workOrder.travelItems.length > 1 ? `#${index + 1}` : ''}</strong>
                            <br />
                            <span className="item-desc">
                              {t('print.travel.fixedCharge')}: {formatCurrency(item.fixedPrice, currencyCountry)}
                              {item.discountPercent > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>1</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(fixedCost, currencyCountry)}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(fixedCost, currencyCountry)}</td>
                        </tr>
                      );
                    } else {
                      // Per-km mode - separate rows for km and time costs
                      const kmCost = item.distance * item.distanceRate * (item.tripMultiplier || 1) * (1 - (item.discountPercent || 0) / 100);
                      const timeCost = item.travelTime * item.timeRate * (item.tripMultiplier || 1) * (1 - (item.discountPercent || 0) / 100);
                      const travelLabel = workOrder.travelItems.length > 1 ? ` #${index + 1}` : '';
                      const isRoundTrip = (item.tripMultiplier || 1) === 2;
                      
                      return (
                        <>
                          {/* Distance cost row */}
                          {item.distance > 0 && (
                            <tr key={`travel-km-${index}`}>
                              <td>
                                <strong>{t('print.travel.kmCost')}{travelLabel}</strong>
                                {isRoundTrip && <><br /><span className="item-desc" style={{ fontWeight: 'bold', color: '#1a73e8' }}>({t('print.travel.roundTrip')})</span></>}
                                <br />
                                <span className="item-desc">
                                  {t('print.travel.kmSummary', { distance: item.distance, rate: item.distanceRate })}
                                  {item.discountPercent > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>{item.distance} km</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(item.distanceRate, currencyCountry)}/km</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(kmCost, currencyCountry)}</td>
                            </tr>
                          )}
                          {/* Time cost row */}
                          {item.travelTime > 0 && (
                            <tr key={`travel-time-${index}`}>
                              <td>
                                <strong>{t('print.travel.timeCost')}{travelLabel}</strong>
                                {isRoundTrip && <><br /><span className="item-desc" style={{ fontWeight: 'bold', color: '#1a73e8' }}>({t('print.travel.roundTrip')})</span></>}
                                <br />
                                <span className="item-desc">
                                  {t('print.travel.timeSummary', { time: item.travelTime, rate: item.timeRate })}
                                  {item.discountPercent > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>{item.travelTime} h</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(item.timeRate, currencyCountry)}/h</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(timeCost, currencyCountry)}</td>
                            </tr>
                          )}
                        </>
                      );
                    }
                  })}

                  {/* Labor Items */}
                  {workOrder.workItems.length > 0 && workOrder.workItems.map((item, index) => {
                    const baseLabor = item.technicians * item.hours * item.hourlyRate;
                    const surchargeMultiplier = (item.nightSunday ? 0.5 : 0) + (item.urgent ? 0.5 : 0);
                    const laborCost = baseLabor * (1 + surchargeMultiplier);
                    const callOutFee = item.customCallOutFee || item.callOutFee;
                    const accommodationCost = (item.accommodationNights * 100) + (item.perDiemDays * 45); // Using default rates

                    const surcharges = [];
                    if (item.nightSunday) surcharges.push('+50% ' + t('print.labor.nightSurcharge'));
                    if (item.urgent) surcharges.push('+50% ' + t('print.labor.urgentSurcharge'));

                    return (
                      <>
                        <tr key={`labor-${index}`}>
                          <td>
                            <strong>{t('print.labor.title')} {workOrder.workItems.length > 1 ? `#${index + 1}` : ''}</strong>
                            <br />
                            <span className="item-desc">
                              {item.technicians} {t('print.labor.technicians')} × {item.hours} {t('print.pricing.hours')} @ {formatCurrency(item.hourlyRate, currencyCountry)}/h
                              {surcharges.length > 0 && ` (${surcharges.join(', ')})`}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{item.hours}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(item.hourlyRate * item.technicians, currencyCountry)}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(laborCost, currencyCountry)}</td>
                        </tr>

                        {callOutFee > 0 && (
                          <tr key={`callout-${index}`}>
                            <td style={{ paddingLeft: '20px' }}>{t('print.labor.callOutFee')}</td>
                            <td style={{ textAlign: 'center' }}>1</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(callOutFee, currencyCountry)}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(callOutFee, currencyCountry)}</td>
                          </tr>
                        )}

                        {accommodationCost > 0 && (
                          <tr key={`accommodation-${index}`}>
                            <td style={{ paddingLeft: '20px' }}>
                              <strong>{t('print.accommodation.title')}</strong>
                              <br />
                              <span className="item-desc">
                                {item.accommodationNights > 0 && `${item.accommodationNights} ${t('print.pricing.nights')}`}
                                {item.accommodationNights > 0 && item.perDiemDays > 0 && ' + '}
                                {item.perDiemDays > 0 && `${item.perDiemDays} ${t('print.accommodation.perDiem')}`}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>1</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(accommodationCost, currencyCountry)}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(accommodationCost, currencyCountry)}</td>
                          </tr>
                        )}
                      </>
                    );
                  })}

                  {/* Spare Parts */}
                  {workOrder.parts.length > 0 && (
                    <>
                      <tr className="section-header-row">
                        <td colSpan={4}><strong>{t('print.parts.title')}</strong></td>
                      </tr>
                      {workOrder.parts.map((part, index) => {
                        const unitPriceWithMarkup = part.unitPrice * (1 + part.markup / 100);
                        const partCost = unitPriceWithMarkup * part.quantity;
                        return (
                          <tr key={`part-${index}`}>
                            <td style={{ paddingLeft: '20px' }}>{part.description}</td>
                            <td style={{ textAlign: 'center' }}>{part.quantity} {t('print.pricing.pcs')}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(unitPriceWithMarkup, currencyCountry)}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(partCost, currencyCountry)}</td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

        {/* Totals Summary */}
        {(workOrder.travelItems.length > 0 || workOrder.workItems.length > 0 ||
          workOrder.parts.length > 0 || workOrder.customLines.length > 0) && (
            <div className="print-totals">
              <div className="totals-table">
                <div className="totals-row">
                  <span>{t('print.pricing.subtotal')}</span>
                  <span>{formatCurrency(workOrder.totals.subtotal, currencyCountry)}</span>
                </div>
                <div className="totals-row">
                  <span>{t('print.pricing.vat')} ({workOrder.totals.vatPercent.toFixed(1)}%)</span>
                  <span>{formatCurrency(workOrder.totals.vat, currencyCountry)}</span>
                </div>
                <div className="totals-row total">
                  <span>{t('print.pricing.grandTotal')}</span>
                  <span>{formatCurrency(workOrder.totals.total, currencyCountry)}</span>
                </div>
              </div>
            </div>
          )}

        {/* Signature Section */}
        <div className="signature-section">
          <h3 className="section-title">{t('print.workOrder.signatures').toUpperCase()}</h3>
          <div className="signature-boxes">
            <div className="signature-box">
              <div className="signature-box-title">{t('print.workOrder.customerSignatureLabel')}</div>
              <div className="signature-line"></div>
              <div className="signature-label">
                {t('print.workOrder.nameInBlockLetters')}
              </div>
              <div className="signature-date">
                <span className="signature-date-label">{t('print.workOrder.date')}:</span>
                <div className="signature-date-line"></div>
              </div>
            </div>

            <div className="signature-box">
              <div className="signature-box-title">{t('print.workOrder.haltonRepresentativeLabel')}</div>
              <div className="signature-line"></div>
              <div className="signature-label">
                {t('print.workOrder.nameInBlockLetters')}
              </div>
              <div className="signature-date">
                <span className="signature-date-label">{t('print.workOrder.date')}:</span>
                <div className="signature-date-line"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <p><strong>{company.name}</strong> | VAT ID: {company.vatId}</p>
          <p>{t('print.footer.thankYou')}</p>
        </div>
      </div>
    </div >
  );
};

export default WorkOrderPrintView;
